import { RuleType } from '@twaice-fe/shared/models';

export enum AlertSeverity {
  High = 'high',
  Medium = 'medium',
  Minor = 'minor',
  Normal = 'normal',
}

export enum ThresholdSeverity {
  High = 'high',
  Medium = 'medium',
  Minor = 'minor',
}

export enum ComparisonOperator {
  LessThan = 'LT',
  LessThanEqual = 'LTE',
  GreaterThan = 'GT',
  GreaterThanEqual = 'GTE',
}

export enum AlertSortBy {
  Severity,
  BreachStatus,
  LastBreach,
  RuleBk,
}

export interface RuleVersion {
  createdAt: Date;
  versionNumber: number;
}

export interface ThresholdRuleVersion extends RuleVersion {
  comparisonOperator: ComparisonOperator;
  highThreshold: number | null;
  mediumThreshold: number | null;
  minorThreshold: number | null;
}

export interface BreachedRuleSummary {
  ruleBk: string;
  ruleName: string;
  highestSeverity: AlertSeverity;
  highestBreachedThreshold: number | null;
  comparisonOperator: ComparisonOperator | null;
  lastBreach: string;
  isOngoing: boolean;
  unit: string | null;
  isBreachOnOldVersion: boolean;
  type: RuleType;
}

export interface BreachedRule {
  componentBk: string;
  levelBk: string;
  ruleBk: string;
  highestSeverity: ThresholdSeverity;
  highestSeverityBreachedRuleVersion?: number;
  lastBreach: Date | string;
  isOngoing: boolean;
  parentComponentBk?: string | null;
}

export interface RuleEvaluation {
  componentBk: string;
  ruleBk: string;
  ruleVersion: number;
  levelBk: string;
  start: Date;
  end: Date;
  state: AlertSeverity;
}

export interface TimeRangeIsoStr {
  start: string;
  end: string;
}

// Only the parts of a rule configuration the side panel reads, so each page can pass its own rule models.
export interface BreachSummaryRuleVersion {
  versionNumber: number;
}

export interface BMSBreachSummaryRule {
  type: RuleType.BMSRule;
  ruleBk: string;
  ruleName: string;
  versions: BreachSummaryRuleVersion[];
}

export interface ThresholdBreachSummaryRule {
  type: RuleType;
  ruleBk: string;
  ruleName: string;
  unit: string | null;
  versions: BreachSummaryRuleVersion[];
  currentVersion: BreachSummaryRuleVersion;
}

export type BreachSummaryRule = BMSBreachSummaryRule | ThresholdBreachSummaryRule;

export interface RuleDetails {
  description: string | null;
}

export interface RuleChatMessagePost {
  message: string;
  tags: string[];
  includeLink: boolean;
  relatedEntities?: string[];
  timestamp?: Date;
}

export interface RuleActivityLink {
  label: string;
  url: string | null;
}

export interface RuleActivity {
  date: Date;
  icon: string;
  // Without one, the title and the time share a single line.
  actor: string | null;
  title: string;
  detail: string | null;
  link: RuleActivityLink | null;
}

export interface SmartAlertsSystemParams {
  customerBk: string;
  systemBk: string;
}

export interface SmartAlertsRuleEvaluationsParams extends SmartAlertsSystemParams {
  ruleBk: string;
  componentBk: string;
  start: string | number | Date;
  end: string | number | Date;
}

export interface SmartAlertsBreachesParams extends SmartAlertsSystemParams {
  date: Date | string;
  ruleTypes: RuleType[];
  componentTypes?: string[] | null;
}

// The API stores advanced conditions per rule version; a rule's current conditions are its latest version's.
export interface SmartAlertsRuleVersion {
  versionNumber: number;
  relationalSensors?: unknown[];
  minDurationSeconds?: number | null;
}

export interface SmartAlertsRule {
  kpi: string;
  ruleName: string;
  versions: unknown[];
}

export interface SmartAlertsRuleEvaluationResponse extends Omit<RuleEvaluation, 'start' | 'end'> {
  start: string;
  end: string;
}

export interface LookbackDaysOption {
  days: number;
  label: string;
}
